import React, { useState } from "react"
import { Tags } from "@tenforce/product-fields"
import { ColorName } from "@tenforce/toolbox-default-style"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { ITag, ITagPickerField } from "../../index"
import { ITenForceWindow } from "../../../../global"
import { Labels } from "../../../../util/Translations"
import {
    fetchFields,
    setCurrentFieldInEditMode,
    updateField,
} from "../../slice"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import { GlobalState } from "../../../../store"
import { formatString } from "../../../../util/Strings"
import {
    createTag,
    deleteTag,
    getTags,
    getTagByExactName,
    updateTag,
} from "../../../../apis/tagPicker"
import { addErrorNotification } from "../../../notifications/actions"

declare let window: ITenForceWindow

interface Props extends ITagPickerField {
    itemId: number
}

export default function TagField(props: Props): JSX.Element {
    const {
        itemId,
        odataType: _odataType,
        value: initialValue,
        isReadOnly = false,
        isRequired = false,
        isLoading = false,
        isDisabled = false,
        isCondensed = false,
        fieldId,
        position: _position,
        type: _type,
        itemType: _itemType,
        ...rest
    } = props

    const dispatch = useAppDispatch()
    const { currentFieldInEditMode = "", isLoading: isFieldLoading = false } =
        useAppSelector((state: GlobalState) => state.fields)

    const [suggestions, setSuggestions] = useState<ITag[]>([])
    const [selectableTags, setSelectableTags] = useState<ITag[]>([])
    const [totalSelectable, setTotalSelectable] = useState<number>(0)
    const [selectedTags, setSelectedTags] = useState<ITag[]>(initialValue)
    const [totalSelected, setTotalSelected] = useState<number>(
        initialValue.length,
    )

    const isLoadingFinal = isLoading || isFieldLoading
    const isDisabledFinal =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    return (
        <Tags
            {...rest}
            fieldId={fieldId}
            isCondensed={isCondensed}
            isDisabled={isDisabledFinal}
            isLoading={isLoadingFinal}
            isReadOnly={isReadOnly}
            isRequired={isRequired}
            onCreateOrAddTag={async (tagValue): Promise<ITag> => {
                try {
                    const existingTag: ITag | null = await getTagByExactName(
                        itemId,
                        fieldId,
                        tagValue,
                    )
                    if (existingTag) {
                        return existingTag
                    }
                    return await createTag({
                        name: tagValue,
                        color: ColorName.Platinum,
                    })
                } catch (error) {
                    console.error(error)
                    dispatch(addErrorNotification(error.message))
                    return Promise.reject(error)
                }
            }}
            onDeleteTag={async (tag): Promise<void> => {
                try {
                    await deleteTag(tag)
                    await dispatch(fetchFields())
                    setSelectableTags(
                        selectableTags.filter(
                            (selectable) => selectable.id !== tag.id,
                        ),
                    )
                    setTotalSelectable(totalSelectable - 1)
                } catch (error) {
                    console.error(error)
                    dispatch(addErrorNotification(error.message))
                }
            }}
            onFetchSuggestions={async (filter): Promise<void> => {
                try {
                    const result = await getTags(
                        itemId,
                        fieldId,
                        initialValue,
                        [],
                        filter,
                        0,
                        10,
                    )
                    const suggestions = (result?.tags ?? []).filter(
                        (suggestion) => suggestion.name !== filter,
                    )
                    setSuggestions(suggestions)
                } catch (error) {
                    console.error(error)
                    dispatch(addErrorNotification(error.message))
                }
            }}
            onSave={async (newValue): Promise<void> => {
                const oldV = initialValue.slice().sort((a, b) => a.id - b.id)
                const newV = newValue.slice().sort((a, b) => a.id - b.id)
                if (JSON.stringify(newV) !== JSON.stringify(oldV)) {
                    await dispatch(
                        updateField({
                            field: props,
                            newValue,
                        }),
                    ).unwrap()
                }
                setSuggestions([])
            }}
            onSearchForTag={async (tag): Promise<void> => {
                try {
                    window.env.searchTag(`${tag.id}`)
                } catch (error) {
                    console.error(error)
                    dispatch(addErrorNotification(error.message))
                }
            }}
            onUpdateTag={async (tag): Promise<void> => {
                try {
                    await updateTag(tag)
                    await dispatch(fetchFields())
                    setSelectableTags(
                        selectableTags.map((selectable) => {
                            if (selectable.id !== tag.id) {
                                return selectable
                            }
                            return tag
                        }),
                    )
                    setSelectedTags(
                        selectedTags.map((selected) => {
                            if (selected.id !== tag.id) {
                                return selected
                            }
                            return tag
                        }),
                    )
                } catch (error) {
                    console.error(error)
                    dispatch(addErrorNotification(error.message))
                }
            }}
            suggestions={suggestions}
            value={initialValue}
            selectableView={{
                options: selectableTags,
                totalCount: totalSelectable,
                fetchOptions: async (filter, skip, limit): Promise<void> => {
                    try {
                        const result = await getTags(
                            itemId,
                            fieldId,
                            [],
                            [],
                            filter,
                            skip,
                            limit,
                        )
                        if (skip === 0) {
                            setSelectableTags(result?.tags ?? [])
                        } else {
                            setSelectableTags([
                                ...selectableTags,
                                ...(result?.tags ?? []),
                            ])
                        }
                        setTotalSelectable(result?.total ?? 0)
                    } catch (error) {
                        console.error(error)
                        dispatch(addErrorNotification(error.message))
                    }
                },
            }}
            selectedView={{
                options: selectedTags,
                totalCount: totalSelected,
                fetchOptions: async (filter, ids): Promise<void> => {
                    try {
                        // If there are no selected tags, no need to fetch anything.
                        if (ids.length === 0) {
                            setSelectedTags([])
                            setTotalSelected(0)
                            return
                        }
                        const result = await getTags(
                            itemId,
                            fieldId,
                            [],
                            ids,
                            filter,
                        )
                        setSelectedTags(result?.tags ?? [])
                        setTotalSelected(result?.total ?? 0)
                    } catch (error) {
                        console.error(error)
                        dispatch(addErrorNotification(error.message))
                    }
                },
            }}
            placeholder={Labels.SelectOrCreateTag}
            labels={{
                Color: Labels.Color,
                CustomizeTags: Labels.ManageAllTags,
                DeleteEverywhere: Labels.DeleteEverywhere,
                EmptyPlaceholder: Labels.ClickHereToEdit,
                FilterAllNValues: (count) =>
                    Symbols.Search +
                    formatString(Labels.FilterAllNValues, count),
                FilterNSelectedValues: (count) =>
                    Symbols.Search +
                    formatString(Labels.FilterNSelectedValues, count),
                FilterNoResults: Labels.NothingFound,
                LoadNMore: (remaining) =>
                    formatString(Labels.LoadNMore, remaining),
                LoadNMoreOf: (pageSize, remaining) =>
                    formatString(Labels.LoadNMoreOf, pageSize, remaining),
                ShowAllValuesTooltip: Labels.ShowAllValues,
                ShowSelectedValuesTooltip: Labels.ShowSelectedValues,
                Remove: Labels.Remove,
                SearchItemsWithTag: Labels.SearchItemsWithTag,
                Title: Labels.Title,
            }}
            onFieldEnterEditMode={(id): void => {
                dispatch(setCurrentFieldInEditMode(id ?? fieldId))
            }}
            onFieldExitEditMode={(): void => {
                dispatch(setCurrentFieldInEditMode(""))
            }}
        />
    )
}
